"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { ChevronDown, Search } from "lucide-react";
import NewsCard from "@/components/shared/NewsCard";
import Pagination from "@/components/ui/pagination";
import { getImageUrl } from "@/utils/utils";
import { staticCategories } from "@/data/shared";
import { useClickOutside } from "@/hooks/useClickOutside";
import Image from "next/image";

const ALL_YEARS = "All Years";
const currentYear = new Date().getFullYear();
const years = [
  ALL_YEARS,
  ...Array.from({ length: currentYear - 2014 + 1 }, (_, i) =>
    (currentYear - i).toString()
  ),
];

interface NewsPageData {
  id: number;
  documentId: string;
  title: string;
  breadcrumb: {
    home: string;
    news: string;
  };
  searchText: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  locale: string;
  backgroundImage: any;
  localizations: any[];
}

interface NewsCategory {
  id: number;
  documentId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  locale: string;
}

interface NewsItem {
  id: number;
  documentId: string;
  title: string;
  excerpt: string;
  content: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  publishedDate: string;
  locale: string;
  images: any[];
  categories: NewsCategory[];
}

interface NewsPageClientProps {
  lang: string;
  locale: string;
  newsPageData?: NewsPageData | null;
  initialNewsData?: NewsItem[];
  initialPagination?: {
    page: number;
    pageSize: number;
    pageCount: number;
    total: number;
  };
  initialSelectedCategory?: string;
  initialSelectedYear?: string;
  initialCurrentPage?: number;
  initialSearchQuery?: string;
  newsError?: string | null;
}

export default function NewsPageClient({
  lang,
  locale,
  newsPageData: initialNewsPageData,
  initialNewsData = [],
  initialPagination = { page: 1, pageSize: 6, pageCount: 1, total: 0 },
  initialSelectedCategory,
  initialSelectedYear = ALL_YEARS,
  initialCurrentPage = 1,
  initialSearchQuery = "",
  newsError,
}: NewsPageClientProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [newsPageData, setNewsPageData] = useState<NewsPageData | null>(
    initialNewsPageData || null
  );
  const [selectedCategory, setSelectedCategory] = useState<string>(
    initialSelectedCategory || ""
  );
  const [selectedYear, setSelectedYear] = useState(initialSelectedYear);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [debouncedSearchQuery, setDebouncedSearchQuery] =
    useState(initialSearchQuery);
  const [currentPage, setCurrentPage] = useState(initialCurrentPage);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [newsData, setNewsData] = useState<NewsItem[]>(initialNewsData);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(initialPagination.pageCount);

  // Use ref to track if we're in the middle of a user-initiated change
  const isUserChangingRef = useRef(false);

  // Add ref to scroll to content section on pagination
  const contentSectionRef = useRef<HTMLDivElement>(null);

  // Add click outside functionality for dropdown
  const yearDropdownRef = useClickOutside<HTMLDivElement>(() => {
    setYearDropdownOpen(false);
  });

  // Update data when props change without causing scroll jumps
  useEffect(() => {
    setNewsData(initialNewsData);
    setTotalPages(initialPagination.pageCount);
  }, [initialNewsData, initialPagination.pageCount]);

  // Update selected category when props change (but not during user interactions)
  useEffect(() => {
    if (
      !isUserChangingRef.current &&
      initialSelectedCategory !== selectedCategory
    ) {
      setSelectedCategory(initialSelectedCategory || "");
    }
  }, [initialSelectedCategory]);

  // Update selected year when props change (but not during user interactions)
  useEffect(() => {
    if (!isUserChangingRef.current && initialSelectedYear !== selectedYear) {
      setSelectedYear(initialSelectedYear);
    }
  }, [initialSelectedYear]);

  // Update current page when props change (but not during user interactions)
  useEffect(() => {
    if (!isUserChangingRef.current && initialCurrentPage !== currentPage) {
      setCurrentPage(initialCurrentPage);
    }
  }, [initialCurrentPage]);

  // Update search query when props change (but not during user interactions)
  useEffect(() => {
    if (!isUserChangingRef.current && initialSearchQuery !== searchQuery) {
      setSearchQuery(initialSearchQuery);
      setDebouncedSearchQuery(initialSearchQuery);
    }
  }, [initialSearchQuery]);

  // Sync state with URL parameters when they change (but not during user interactions)
  useEffect(() => {
    if (!isUserChangingRef.current) {
      const categoryParam = searchParams.get("category");
      const yearParam = searchParams.get("year");
      const pageParam = searchParams.get("page");
      const searchParam = searchParams.get("search");

      if (categoryParam && categoryParam !== selectedCategory) {
        setSelectedCategory(categoryParam);
      }

      if (yearParam && yearParam !== selectedYear) {
        setSelectedYear(yearParam);
      }

      if (pageParam && parseInt(pageParam) !== currentPage) {
        setCurrentPage(parseInt(pageParam));
      }

      if (searchParam && searchParam !== searchQuery) {
        setSearchQuery(searchParam);
        setDebouncedSearchQuery(searchParam);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    scrollTo(0, 0);
  }, []);

  // Debounce search query and navigate to new URL
  useEffect(() => {
    // Show search loading when user is typing (but not on initial load or other filter changes)
    if (searchQuery !== debouncedSearchQuery && searchQuery.length > 0) {
      setSearchLoading(true);
    }

    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      // Reset to first page when search query changes
      if (searchQuery !== debouncedSearchQuery) {
        setCurrentPage(1);
        // Navigate to new URL with search query
        handleFilterChange(selectedCategory, selectedYear, 1, searchQuery);
      }
      setSearchLoading(false);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(timer);
      setSearchLoading(false);
    };
  }, [searchQuery]);

  // No client-side data fetching - let SSR handle it

  // Navigation handler for filters and pagination
  const handleFilterChange = (
    newCategory: string,
    newYear: string,
    newPage: number = 1,
    newSearch: string = ""
  ) => {
    const urlParams = new URLSearchParams();

    const allNewsLabel = locale === "jp" ? "すべてのニュース" : "All News";
    if (newCategory && newCategory !== allNewsLabel) {
      urlParams.set("category", encodeURIComponent(newCategory));
    }

    if (newYear && newYear !== ALL_YEARS) {
      urlParams.set("year", newYear);
    }

    if (newSearch) {
      urlParams.set("search", newSearch);
    }

    if (newPage > 1) {
      urlParams.set("page", newPage.toString());
    }
    urlParams.set("_t", Date.now().toString());


    const queryString = urlParams.toString();
    const newUrl = queryString
      ? `/${locale}/news?${queryString}`
      : `/${locale}/news`;

    router.replace(newUrl, { scroll: false });
  };

  // Handle category selection
  const handleCategoryChange = (category: string) => {
    // Mark that user is changing to prevent prop overrides
    isUserChangingRef.current = true;

    setSelectedCategory(category);
    handleFilterChange(category, selectedYear, 1, debouncedSearchQuery);

    // Reset the flag after URL update completes
    setTimeout(() => {
      isUserChangingRef.current = false;
    }, 200);
  };

  // Handle year selection
  const handleYearChange = (year: string) => {
    // Mark that user is changing to prevent prop overrides
    isUserChangingRef.current = true;

    setSelectedYear(year);
    setCurrentPage(1);
    handleFilterChange(selectedCategory, year, 1, debouncedSearchQuery);

    // Reset the flag after URL update completes
    setTimeout(() => {
      isUserChangingRef.current = false;
    }, 200);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handleFilterChange(
      selectedCategory,
      selectedYear,
      page,
      debouncedSearchQuery
    );

    // Scroll to the top of the content section when page changes
    // Use setTimeout to ensure DOM has updated after navigation
    setTimeout(() => {
      if (contentSectionRef.current) {
        contentSectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100);
  };

  const displayedArticles = useMemo(() => newsData || [], [newsData]);

  // Get localized text from news page data or fallbacks
  const searchPlaceholder =
    newsPageData?.searchText || (locale === "jp" ? "検索..." : "Search...");
  const noResultsMessage =
    locale === "jp" ? "条件に一致する記事が見つかりません。" : "No data found!";

  // Helper function to format date
  const formatDate = useMemo(
    () => (dateString: string) => {
      const date = new Date(dateString);
      if (locale === "jp") {
        return date.toLocaleDateString("ja-JP");
      }
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    [locale]
  );

  // Get first category name for each article
  const getArticleCategory = useMemo(
    () => (article: NewsItem) => {
      return article.categories?.[0]?.title || "";
    },
    []
  );

  // Show error state if news data fetching failed
  if (newsError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">{newsError}</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a5852] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#f4f5f6] py-20">
      <div className="mx-auto">
        <div className="bg-white relative overflow-hidden group/progress">
          {/* Background Pattern */}
          <Image
            src="/images/shared/page-blend-bg.svg"
            alt="Background pattern"
            fill
            className="object-cover opacity-30 luminosity mix-blend-luminosity pointer-events-none select-none"
          />

          <div
            ref={contentSectionRef}
            className="relative p-4 sm:p-6 lg:p-[50px] lg:min-h-[682px]"
          >
            {/* Header Controls */}
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-8">
              {/* Year Dropdown */}
              <div className="relative w-full sm:w-auto" ref={yearDropdownRef}>
                <button
                  onClick={() => setYearDropdownOpen(!yearDropdownOpen)}
                  className="flex items-center justify-between w-full lg:w-[200px] px-4 py-3 border border-[#dee1e5] rounded bg-white text-[#0a1119] text-base cursor-pointer"
                >
                  <span>{selectedYear}</span>
                  <ChevronDown className="w-[14px] h-[14px] text-[#88909f]" />
                </button>

                {yearDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-full lg:w-[200px] bg-white border border-[#dee1e5] rounded shadow-lg z-10 overflow-y-auto max-h-[250px]">
                    {years.map((year) => (
                      <button
                        key={year}
                        onClick={() => {
                          handleYearChange(year);
                          setYearDropdownOpen(false);
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-[#f4f5f6] text-[#0a1119] text-base cursor-pointer"
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-4 sm:gap-x-6 sm:gap-y-2 lg:gap-x-8 lg:gap-y-2 order-3 lg:order-2">
                {/* Static categories */}
                {(staticCategories || []).map(
                  (category: string, index: number) => (
                    <button
                      key={index}
                      onClick={() => handleCategoryChange(category)}
                      className={`relative text-sm sm:text-base font-medium capitalize transition-colors whitespace-nowrap cursor-pointer before:content-[''] before:absolute before:bottom-[-2px] before:left-0 before:w-0 before:h-[2px] before:bg-[#1a5852] before:transition-all before:duration-300 before:ease-out hover:before:w-full ${
                        selectedCategory === category
                          ? "text-[#1a5852]"
                          : "text-[#0a1119] hover:text-[#1a5852]"
                      }`}
                    >
                      {category}
                      {selectedCategory === category && (
                        <div className="absolute bottom-[-2px] left-0 w-full h-[2px] bg-[#1a5852]"></div>
                      )}
                    </button>
                  )
                )}
              </div>

              {/* Search Box */}
              <div className="relative w-full sm:w-auto order-2 lg:order-3">
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full lg:w-[200px] px-4 py-3 bg-[#f4f5f6] border border-[#dee1e5] rounded-[20px] text-sm text-[#88909f] placeholder-[#88909f] pr-10 focus:outline-none"
                />
                {searchLoading ? (
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#1a5852]"></div>
                  </div>
                ) : (
                  <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#88909f]" />
                )}
              </div>
            </div>

            {/* Loading State */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a5852]"></div>
              </div>
            ) : (
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: { opacity: 0 },
                  visible: {
                    opacity: 1,
                    transition: {
                      staggerChildren: 0.1,
                    },
                  },
                }}
              >
                {/* News Grid */}
                {displayedArticles.map((article) => (
                  <motion.div
                    key={article.id}
                    variants={{
                      hidden: { opacity: 0, y: 30 },
                      visible: { opacity: 1, y: 0 },
                    }}
                    transition={{ duration: 0.5 }}
                  >
                    <NewsCard
                      image={getImageUrl(article.images?.[0]?.url || "")}
                      title={article.title}
                      category={getArticleCategory(article)}
                      date={
                        article?.publishedDate
                          ? formatDate(article?.publishedDate)
                          : null
                      }
                      excerpt={article.excerpt}
                      alt={article.title}
                      href={`/${lang}/news/${article.slug}`}
                    />
                  </motion.div>
                ))}
              </motion.div>
            )}

            {/* Empty State */}
            {!loading && displayedArticles.length === 0 && (
              <div className="text-center flex items-center justify-center h-[472px] lg:h-[500px]">
                <p className="text-[#88909f] text-lg">{noResultsMessage}</p>
              </div>
            )}

            {/* Pagination with 32px gap from news cards */}
            {!loading && totalPages > 1 && (
              <div style={{ marginTop: "32px" }}>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>

          {/* Progress Bar */}
          <div className="flex h-[2px] bg-gray-25 relative">
            <div
              className={`h-[2px] bg-[#1c8579] absolute left-0 top-0 transition-all duration-500 w-0 group-hover/progress:w-full`}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
}
